/**
 * Controls Page - SOC 2 Control Registry
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Download, ChevronRight, Clock, CheckCircle2, X, Loader2 } from 'lucide-react';
import { CATEGORIES, STATUS_COLORS, Card, Badge } from '../components/ui-custom/ComplianceUI';
import SearchBar from '../components/common/SearchBar';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function ControlEditModal({ control, onClose, onSave, onMarkReviewed, saving }) {
  const [status, setStatus] = useState(control.status || 'manual_review');
  const [notes, setNotes] = useState(control.notes || '');
  const [owner, setOwner] = useState(control.owner || '');

  const statusOptions = [
    { value: 'passing', label: 'Passing', color: '#059669' },
    { value: 'failing', label: 'Failing', color: '#dc2626' },
    { value: 'warning', label: 'Warning', color: '#d97706' },
    { value: 'manual_review', label: 'Manual Review Required', color: '#7c3aed' },
    { value: 'not_implemented', label: 'Not Implemented', color: '#6b7280' },
    { value: 'not_applicable', label: 'Not Applicable', color: '#9ca3af' },
  ];

  const handleSave = () => {
    onSave({ status, notes, owner });
  };

  const meta = CATEGORIES[control.category] || { label: control.category, color: '#6b7280' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content control-editor" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-control-id" style={{ color: meta.color }}>{control.control_id}</div>
            <h2>{control.title}</h2>
          </div>
          <button className="modal-close" onClick={onClose} data-testid="close-control-modal">
            <X size={20} />
          </button>
        </div>
        
        <div className="modal-body">
          {/* Control Info */}
          <div className="control-detail-section">
            <h3>Control Details</h3>
            <div className="control-detail-grid">
              <div className="detail-item">
                <label>Category</label>
                <span style={{ color: meta.color }}>{meta.label}</span>
              </div>
              <div className="detail-item">
                <label>Environment</label>
                <span>{control.environment === 'both' ? 'Both' : control.environment?.replace(/_/g, ' ')}</span>
              </div>
              <div className="detail-item">
                <label>Evidence Type</label>
                <span>{control.evidence_type || 'Manual'}</span>
              </div>
              <div className="detail-item">
                <label>Last Reviewed</label>
                <span>{control.last_reviewed ? new Date(control.last_reviewed).toLocaleDateString() : 'Never'}</span>
              </div>
            </div>
            {control.description && (
              <div className="control-description">
                <label>Description</label>
                <p>{control.description}</p>
              </div>
            )}
          </div>

          {/* Status Update */}
          <div className="control-detail-section">
            <h3>Update Status</h3>
            <div className="status-options">
              {statusOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setStatus(opt.value)}
                  className={`status-option ${status === opt.value ? 'selected' : ''}`}
                  style={{ 
                    borderColor: status === opt.value ? opt.color : 'transparent',
                    background: status === opt.value ? opt.color + '20' : 'var(--card-bg)'
                  }}
                  data-testid={`status-${opt.value}`}
                >
                  <span className="status-dot" style={{ background: opt.color }} />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Owner */}
          <div className="control-detail-section">
            <h3>Owner</h3>
            <input
              type="text"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              placeholder="e.g., IT Security Team, mike@eyvo.com"
              className="control-input"
              data-testid="control-owner-input"
            />
          </div>

          {/* Notes */}
          <div className="control-detail-section">
            <h3>Review Notes</h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about evidence reviewed, implementation details, or next steps..."
              className="control-textarea"
              rows={4}
              data-testid="control-notes-input"
            />
          </div>

          {/* Implementations */}
          {control.implementations && control.implementations.length > 0 && (
            <div className="control-detail-section">
              <h3>Implementations</h3>
              <ul className="implementations-list">
                {control.implementations.map((impl, i) => (
                  <li key={i}>{impl}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="btn-secondary" 
            onClick={onMarkReviewed}
            disabled={saving}
            data-testid="mark-reviewed-btn"
          >
            <Clock size={16} /> Mark Reviewed
          </button>
          <div className="footer-right">
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button 
              className="btn-primary" 
              onClick={handleSave}
              disabled={saving}
              data-testid="save-control-btn"
            >
              {saving ? <Loader2 className="spin" size={16} /> : <CheckCircle2 size={16} />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ControlsPage() {
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedControl, setSelectedControl] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchControls = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.set('category', categoryFilter);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      
      
      const response = await fetch(`${API_URL}/api/controls/?${params}`, {
        credentials: 'include',
        
      });
      if (response.ok) {
        const data = await response.json();
        setControls(data.controls || []);
      }
    } catch (error) {
      console.error('Failed to fetch controls:', error);
      toast.error('Failed to load controls');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, statusFilter]);

  useEffect(() => {
    fetchControls();
  }, [fetchControls]);

  const handleControlClick = async (control) => {
    try {
      
      const response = await fetch(`${API_URL}/api/controls/${control.control_id}`, {
        credentials: 'include',
        
      });
      if (response.ok) {
        const fullControl = await response.json();
        setSelectedControl(fullControl);
        setEditModalOpen(true);
      }
    } catch (error) {
      toast.error('Failed to load control details');
    }
  };

  const handleUpdateControl = async (updates) => {
    if (!selectedControl) return;
    setSaving(true);
    
    try {
      
      const response = await fetch(`${API_URL}/api/controls/${selectedControl.control_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          
        },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      
      if (response.ok) {
        toast.success(`Control ${selectedControl.control_id} updated`);
        setEditModalOpen(false);
        fetchControls();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update control');
      }
    } catch (error) {
      toast.error('Failed to update control');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReviewed = async () => {
    if (!selectedControl) return;
    setSaving(true);
    
    try {
      
      const response = await fetch(`${API_URL}/api/controls/${selectedControl.control_id}/review`, {
        method: 'POST',
        credentials: 'include',
        
      });
      
      if (response.ok) {
        toast.success(`Control ${selectedControl.control_id} marked as reviewed`);
        setSelectedControl(prev => ({ ...prev, last_reviewed: new Date().toISOString() }));
        fetchControls();
      } else {
        toast.error('Failed to mark as reviewed');
      }
    } catch (error) {
      toast.error('Failed to mark as reviewed');
    } finally {
      setSaving(false);
    }
  };

  const handleExportControls = async (format) => {
    try {
      const response = await fetch(
        `${API_URL}/api/controls/export?format=${format}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        toast.error('Export failed');
        return;
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eyvo_soc2_controls.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Exported controls as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed');
    }
  };

  const filteredControls = (() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return controls;
    const tokens = q.split(/\s+/).filter(Boolean);
    return controls.filter((c) => {
      const hay = [
        c.control_id || '',
        c.title || '',
        c.category || '',
        c.environment || '',
        c.status || '',
      ].join(' ').toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  })();

  return (
    <div className="controls-page">
      <div className="page-header">
        <div>
          <h1>Control Registry</h1>
          <p>51 SOC 2 controls mapped to Eyvo implementations — click any control to review or update</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => handleExportControls('csv')} data-testid="export-controls-csv">
            <Download size={16} /> Export CSV
          </button>
          <button className="btn-secondary" onClick={() => handleExportControls('json')} data-testid="export-controls-json">
            <Download size={16} /> Export JSON
          </button>
        </div>
      </div>

      {/* Search */}
      <SearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder='Find a control — try "encryption" or "CC6.1"'
        totalCount={controls.length}
        filteredCount={filteredControls.length}
        testIdPrefix="control-search"
      />

      {/* Filters */}
      <div className="filter-bar">
        {[{ key: 'all', label: 'All' }, ...Object.entries(CATEGORIES).map(([k, v]) => ({ key: k, label: v.label }))].map(f => (
          <button
            key={f.key}
            onClick={() => setCategoryFilter(f.key)}
            className={`filter-btn ${categoryFilter === f.key ? 'active' : ''}`}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Status Filter */}
      <div className="filter-bar" style={{ marginTop: 8 }}>
        {[
          { key: 'all', label: 'All Statuses' },
          { key: 'passing', label: 'Passing' },
          { key: 'manual_review', label: 'Needs Review' },
          { key: 'failing', label: 'Failing' },
          { key: 'not_implemented', label: 'Not Implemented' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`filter-btn ${statusFilter === f.key ? 'active' : ''}`}
            data-testid={`status-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Controls List */}
      <div className="controls-list">
        {loading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : filteredControls.length === 0 ? (
          <Card className="empty-state" data-testid="control-search-empty" style={{ padding: 32, textAlign: 'center' }}>
            <h3 style={{ margin: 0 }}>
              {searchQuery ? `No controls match "${searchQuery}"` : 'No controls match the current filters'}
            </h3>
            <p style={{ marginTop: 6, color: '#94a3b8' }}>Try a different keyword or clear filters.</p>
          </Card>
        ) : (
          filteredControls.map((control) => {
            const meta = CATEGORIES[control.category] || { color: "#6b7280" };
            return (
              <Card 
                key={control.control_id} 
                className="control-card clickable"
                onClick={() => handleControlClick(control)}
                data-testid={`control-${control.control_id}`}
              >
                <div className="control-id" style={{ color: meta.color }}>{control.control_id}</div>
                <div className="control-info">
                  <div className="control-title">{control.title}</div>
                  <div className="control-env">
                    {control.environment === 'both' ? 'Both environments' : 
                     control.environment === 'new_eyvo' ? 'New Eyvo' :
                     control.environment === 'legacy_eyvo' ? 'Legacy Azure' : 'Organizational'}
                  </div>
                </div>
                <Badge color={STATUS_COLORS[control.status] || "#6b7280"}>
                  {control.status?.replace(/_/g, ' ')}
                </Badge>
                <Badge color="#6b7280">{control.evidence_type || 'manual'}</Badge>
                <ChevronRight size={16} className="control-arrow" />
              </Card>
            );
          })
        )}
      </div>

      {/* Control Detail/Edit Modal */}
      {editModalOpen && selectedControl && (
        <ControlEditModal
          control={selectedControl}
          onClose={() => setEditModalOpen(false)}
          onSave={handleUpdateControl}
          onMarkReviewed={handleMarkReviewed}
          saving={saving}
        />
      )}
    </div>
  );
}
